import { TelemetryEvent } from "../Constants";
import { guid } from "../Utils";
import { TelemetryLogger } from "../TelemetryLogger";

const HTTP_TRANSPORT_DEFAULT_TIMEOUT = 5000; // default timeout for fetching

/**
 * This class is the client to perform http request
 * It supports retry and timeout if needed.
 */
export class HttpTransport {
	public requestId: string;
	
	/**
	 * Perform the GET request
	 * @param requestUrl request URL
	 * @param retry maximum retry if failure. For example: 0 means no retry and 1 means retry once if failed
	 * @param timeout Timeout of the request
	 * @param simpleRequestEnabled Simple request if this is true
	 * @returns the response if success. Otherwise, reject with error
	 */
	public getRequestWithRetry(requestUrl: string, retry: number, timeout: number, simpleRequestEnabled = false): Promise<Response> {
		return new Promise((resolve, reject) => {
			let timeBefore = performance.now();
			this.sendGetRequest(requestUrl, true, timeout, simpleRequestEnabled)
			.then((response) => {
				const serviceCallTime = performance.now() - timeBefore;
				TelemetryLogger.getTelemetryLogger().log_Event(
					TelemetryEvent.HttpTransport.GET.Success,
					{
						TimeMilliseconds: serviceCallTime,
						Count: retry,
						Message: JSON.stringify({
							RequestUrl: requestUrl,
							CorrelationId: this.requestId,
							Timeout: timeout,
							SimpleRequestEnabled: simpleRequestEnabled,
						}),
					});
				resolve(response);
			})
			.catch((error) => {
				const serviceCallTime = performance.now() - timeBefore;
				TelemetryLogger.getTelemetryLogger().log_Error(
					TelemetryEvent.HttpTransport.GET.Error,
					JSON.stringify({
						ErrorMessage: error.toString(),
						TimeMilliseconds: serviceCallTime,
						Timeout: timeout,
						Count: retry,
						CorrelationId: this.requestId,
						Message: requestUrl,
						SimpleRequestEnabled: simpleRequestEnabled,
					}),
				);

				if (retry <= 1) {
					return reject(error);
				}
				timeBefore = performance.now();
				this.getRequestWithRetry(requestUrl, retry - 1, timeout, simpleRequestEnabled)
				.then(resolve)
				.catch(reject);
			});
		});
	}

	private createHeaders(simpleRequestEnabled: boolean): Headers {
		const headers: Headers = typeof Headers !== "undefined" && new Headers();

		if (headers) {
			// Create header based on the request
			if (simpleRequestEnabled) {
				this.createSimpleHeaders(headers);
			} else {
				this.createNormalHeaders(headers);
			}
		}
		return headers;
	}

	private createNormalHeaders(headers: Headers) {
		this.requestId = guid();
		headers.append("X-Request-ID", this.requestId);
		headers.append("Content-Type", "application/json");
	}

	private createSimpleHeaders(headers: Headers) {
		this.requestId = guid();
		headers.append("Content-Type", "text/plain");
	}

	public async sendGetRequest(requestUrl: string, useTimeout?: boolean, timeout?: number, simpleRequestEnabled?: boolean): Promise<Response> {
		const requestInit = {
			method: "GET",
			headers: this.createHeaders(simpleRequestEnabled),
		};

		return useTimeout ?
			this.timeoutFetch(requestUrl, requestInit, timeout ?? HTTP_TRANSPORT_DEFAULT_TIMEOUT) :
			fetch(requestUrl, requestInit);
	}
	
	private timeoutFetch(input: RequestInfo, init: RequestInit = {}, timeout: number): Promise<Response> {
		const controller = new AbortController();
		init.signal = controller.signal;
		// Reject after timeout
		const timeoutId = setTimeout(() => controller.abort(), timeout);
		return fetch(input, init).then(response => {
			clearTimeout(timeoutId);
			return response;
		});
	}
}
